/**
 * ShopHome - Landing page with 3 entry points
 * Fun, vibrant, and full of personality!
 */
import React, { useState, useEffect } from 'react';
import { FONT_FAMILY, SHOP_SPACING } from '../ShopStyles';

const ShopHome = ({ onNavigate }) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const entryPoints = [
    {
      id: 'upload',
      icon: '📸',
      title: 'Upload Your Pet',
      description: 'Got a furry friend? Upload their photo and we\'ll transform them into amazing art!',
      cta: 'Start Creating',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      delay: 0,
      tilt: -2, // Tilted left
    },
    {
      id: 'gallery',
      icon: '✨',
      title: 'Get Inspired',
      description: 'Browse our gallery of pet portraits. Find a style you love and use it for your own pet!',
      cta: 'Explore Gallery',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      delay: 100,
      tilt: 1.5, // Tilted right slightly
    },
    {
      id: 'products',
      icon: '🛍️',
      title: 'Shop Products',
      description: 'Clothing, mugs, canvas prints and more! Pick a product first, then add your pet.',
      cta: 'Browse Products',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      delay: 200,
      tilt: -1, // Tilted left slightly
    },
  ];

  const products = [
    { emoji: '👕', name: 'Clothing', color: '#FF6B6B', tilt: -3, category: 'apparel' },
    { emoji: '☕', name: 'Mugs', color: '#4ECDC4', tilt: 2, category: 'drinkware' },
    { emoji: '🖼️', name: 'Canvas', color: '#FFE66D', tilt: -1.5, category: 'prints' },
    {
      emoji: '📱',
      name: 'Phone Cases',
      color: '#95E1D3',
      tilt: 2.5,
      category: 'accessories',
      scrollToProductId: 'phone-case',
    },
    { emoji: '🎒', name: 'Accessories', color: '#DDA0DD', tilt: -2, category: 'accessories' },
    { emoji: '🛋️', name: 'Pillows', color: '#F7DC6F', tilt: 1, category: 'home' },
  ];

  return (
    <div style={styles.container}>
      {/* Floating decorative elements */}
      <div style={styles.floatingPaw1}>🐾</div>
      <div style={styles.floatingPaw2}>🐾</div>
      <div style={styles.floatingHeart}>💖</div>
      <div style={styles.floatingStar}>⭐</div>
      <div style={styles.floatingSparkle1}>✨</div>
      <div style={styles.floatingSparkle2}>✨</div>
      <div style={styles.floatingDog}>🐕</div>
      <div style={styles.floatingCat}>🐱</div>

      {/* Hero Section */}
      <div style={{
        ...styles.hero,
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>
        <div style={{
          ...styles.heroBadge,
          transform: mounted ? 'rotate(-2deg)' : 'rotate(-2deg) translateY(-20px)',
          opacity: mounted ? 1 : 0,
          transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 100ms',
        }}>
          <span style={styles.heroBadgeIcon}>🐕</span>
          <span>Over 10,000 Happy Pets!</span>
        </div>
        <h1 style={styles.heroTitle}>
          Turn Your Pet Into
          <br />
          <span style={styles.heroHighlight}>Amazing Products!</span>
          <span style={styles.heroSparkle}>✨</span>
        </h1>
        <p style={styles.heroSubtitle}>
          Upload a photo, pick a style, and create custom products 
          <br />featuring your furry friend! 🎨
        </p>
      </div>

      {/* Entry Points Grid */}
      <div style={styles.entryGrid}>
        {entryPoints.map((entry, index) => (
          <div
            key={entry.id}
            style={{
              ...styles.entryCard,
              opacity: mounted ? 1 : 0,
              transform: mounted 
                ? (hoveredCard === entry.id 
                    ? `translateY(-16px) scale(1.05) rotate(${entry.tilt * 1.5}deg)` 
                    : `translateY(0) scale(1) rotate(${entry.tilt}deg)`)
                : `translateY(50px) scale(0.9) rotate(${entry.tilt}deg)`,
              transition: `all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) ${entry.delay}ms`,
              boxShadow: hoveredCard === entry.id 
                ? '0 30px 60px -15px rgba(0,0,0,0.3), 0 0 0 4px rgba(255,255,255,0.6)' 
                : '0 15px 40px -10px rgba(0,0,0,0.2)',
            }}
            onMouseEnter={() => setHoveredCard(entry.id)}
            onMouseLeave={() => setHoveredCard(null)}
            onClick={() => onNavigate?.(entry.id)}
          >
            {/* Gradient Background */}
            <div style={{ ...styles.cardGradient, background: entry.gradient }} />
            
            {/* Shimmer effect */}
            <div style={{
              ...styles.cardShimmer,
              opacity: hoveredCard === entry.id ? 1 : 0,
            }} />
            
            {/* Content */}
            <div style={styles.cardContent}>
              <span style={{
                ...styles.cardIcon,
                animation: hoveredCard === entry.id ? 'bounce 0.6s ease infinite' : 'none',
              }}>
                {entry.icon}
              </span>
              <h2 style={styles.cardTitle}>{entry.title}</h2>
              <p style={styles.cardDescription}>{entry.description}</p>
              <button 
                style={{
                  ...styles.cardButton,
                  background: hoveredCard === entry.id ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)',
                  transform: hoveredCard === entry.id ? 'scale(1.05)' : 'scale(1)',
                }}
              >
                {entry.cta} →
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Features Banner - Horizontal strip */}
      <div style={{
        ...styles.featuresBanner,
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.5s ease 400ms',
      }}>
        {[
          { icon: '🎨', text: 'AI Art Magic', color: '#9333ea' },
          { icon: '⚡', text: 'Ready in Minutes', color: '#f59e0b' },
          { icon: '🚚', text: 'Ships Worldwide', color: '#10b981' },
          { icon: '💖', text: '100% Love It', color: '#ec4899' },
        ].map((feature, idx) => (
          <div key={idx} style={styles.featureItem}>
            <span style={styles.featureIcon}>{feature.icon}</span>
            <span style={{ ...styles.featureText, color: feature.color }}>{feature.text}</span>
          </div>
        ))}
      </div>

      {/* Popular Products - Fun scattered photos */}
      <div style={{
        ...styles.productsSection,
        opacity: mounted ? 1 : 0,
        transition: 'all 0.6s ease 500ms',
      }}>
        <h3 style={styles.sectionTitle}>
          <span style={styles.titleEmoji}>✨</span>
          What Will You Create?
          <span style={styles.titleEmoji}>🎨</span>
        </h3>
        <p style={styles.sectionSubtitle}>Pick a product and add your pet's portrait!</p>
        <div style={styles.productsScroll}>
          {products.map((product, idx) => (
            <ProductStack 
              key={`${product.category}-${product.scrollToProductId || 'default'}`}
              product={product} 
              index={idx}
              delay={idx * 100}
              mounted={mounted}
              onClick={() =>
                onNavigate?.('products', {
                  shopCategory: product.category,
                  ...(product.scrollToProductId
                    ? { scrollToProductId: product.scrollToProductId }
                    : {}),
                })
              }
            />
          ))}
        </div>
      </div>

      {/* Fun CTA Banner */}
      <div style={{
        ...styles.ctaBanner,
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'rotate(-1deg)' : 'translateY(30px) rotate(-1deg)',
        transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 600ms',
      }}>
        <span style={styles.ctaEmoji}>🐾</span>
        <div>
          <h4 style={styles.ctaTitle}>Ready to make something pawsome?</h4>
          <p style={styles.ctaSubtitle}>Your pet deserves to be a star!</p>
        </div>
        <button 
          style={styles.ctaButton}
          onClick={() => onNavigate?.('upload')}
        >
          Get Started Free! 🚀
        </button>
      </div>
    </div>
  );
};

// Product stack - fun polaroid style with personality
const ProductStack = ({ product, index, delay, mounted, onClick }) => {
  const [hovered, setHovered] = useState(false);
  
  // Each card gets a unique tilt for scattered look
  const tilts = [-6, 4, -3, 5, -4, 3];
  const tilt = tilts[index % tilts.length];
  
  // Vertical offsets for wave effect
  const offsets = [0, -15, 5, -10, 8, -5];
  const offset = offsets[index % offsets.length];
  
  return (
    <div
      style={{
        ...styles.productStack,
        transform: mounted 
          ? (hovered 
              ? `translateY(${offset - 20}px) rotate(0deg) scale(1.08)` 
              : `translateY(${offset}px) rotate(${tilt}deg) scale(1)`)
          : `translateY(60px) rotate(${tilt}deg) scale(0.8)`,
        opacity: mounted ? 1 : 0,
        transitionDelay: `${delay}ms`,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      {/* Polaroid frame */}
      <div style={{
        ...styles.polaroid,
        boxShadow: hovered 
          ? `0 25px 50px -12px ${product.color}40, 0 0 0 3px ${product.color}`
          : '0 10px 30px -5px rgba(0,0,0,0.15)',
      }}>
        {/* Photo area with gradient */}
        <div style={{
          ...styles.polaroidPhoto,
          background: `linear-gradient(135deg, ${product.color}20 0%, ${product.color}40 100%)`,
        }}>
          <span style={{
            ...styles.polaroidEmoji,
            transform: hovered ? 'scale(1.2) rotate(-10deg)' : 'scale(1) rotate(0deg)',
          }}>
            {product.emoji}
          </span>
          
          {/* Sparkle on hover */}
          {hovered && (
            <span style={styles.sparkleOverlay}>✨</span>
          )}
        </div>
        
        {/* Label area */}
        <div style={styles.polaroidLabel}>
          <span style={{
            ...styles.polaroidText,
            color: hovered ? product.color : '#333',
          }}>
            {product.name}
          </span>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: `${SHOP_SPACING.lg} ${SHOP_SPACING.md}`,
    position: 'relative',
    overflow: 'visible', // Allow hover effects to show
  },
  // Floating decorative elements
  floatingPaw1: {
    position: 'absolute',
    top: '8%',
    left: '3%',
    fontSize: '50px',
    opacity: 0.25,
    animation: 'wiggle 3s ease-in-out infinite',
    transform: 'rotate(-20deg)',
    pointerEvents: 'none',
  },
  floatingPaw2: {
    position: 'absolute',
    top: '55%',
    right: '4%',
    fontSize: '60px',
    opacity: 0.2,
    animation: 'wiggle 4s ease-in-out infinite',
    animationDelay: '-1s',
    transform: 'rotate(15deg)',
    pointerEvents: 'none',
  },
  floatingHeart: {
    position: 'absolute',
    top: '20%',
    right: '12%',
    fontSize: '40px',
    opacity: 0.3,
    animation: 'bounce 2s ease-in-out infinite',
    transform: 'rotate(10deg)',
    pointerEvents: 'none',
  },
  floatingStar: {
    position: 'absolute',
    bottom: '30%',
    left: '10%',
    fontSize: '35px',
    opacity: 0.2,
    animation: 'sparkle 2s ease-in-out infinite',
    pointerEvents: 'none',
  },
  floatingSparkle1: {
    position: 'absolute',
    top: '15%',
    right: '25%',
    fontSize: '28px',
    opacity: 0.25,
    animation: 'sparkle 1.5s ease-in-out infinite',
    animationDelay: '-0.5s',
    pointerEvents: 'none',
  },
  floatingSparkle2: {
    position: 'absolute',
    bottom: '20%',
    right: '20%',
    fontSize: '24px',
    opacity: 0.2,
    animation: 'sparkle 2s ease-in-out infinite',
    animationDelay: '-1s',
    pointerEvents: 'none',
  },
  floatingDog: {
    position: 'absolute',
    top: '45%',
    left: '3%',
    fontSize: '45px',
    opacity: 0.15,
    animation: 'wiggle 4s ease-in-out infinite',
    transform: 'rotate(-15deg)',
    pointerEvents: 'none',
  },
  floatingCat: {
    position: 'absolute',
    top: '70%',
    right: '5%',
    fontSize: '40px',
    opacity: 0.15,
    animation: 'wiggle 3.5s ease-in-out infinite',
    animationDelay: '-1.5s',
    transform: 'rotate(10deg)',
    pointerEvents: 'none',
  },
  hero: {
    textAlign: 'center',
    marginBottom: SHOP_SPACING.xxl,
    padding: `0 ${SHOP_SPACING.lg}`,
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    padding: '8px 20px',
    borderRadius: '50px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#92400e',
    marginBottom: SHOP_SPACING.lg,
    boxShadow: '0 4px 15px rgba(251, 191, 36, 0.3)',
    fontFamily: FONT_FAMILY,
  },
  heroBadgeIcon: {
    fontSize: '20px',
  },
  heroTitle: {
    fontSize: 'clamp(36px, 6vw, 64px)',
    fontWeight: '800',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: SHOP_SPACING.md,
    lineHeight: 1.1,
    position: 'relative',
  },
  heroHighlight: {
    background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 50%, #06b6d4 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    display: 'inline-block',
  },
  heroSparkle: {
    position: 'absolute',
    fontSize: '40px',
    marginLeft: '10px',
    animation: 'sparkle 1.5s ease-in-out infinite',
  },
  heroSubtitle: {
    fontSize: '20px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    margin: 0,
    lineHeight: 1.6,
  },
  entryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: SHOP_SPACING.xl,
    marginBottom: SHOP_SPACING.xxl,
    padding: SHOP_SPACING.md, // Padding for hover overflow
  },
  entryCard: {
    position: 'relative',
    borderRadius: '28px',
    overflow: 'hidden',
    minHeight: '340px',
    cursor: 'pointer',
    border: '3px solid rgba(255,255,255,0.5)',
  },
  cardGradient: {
    position: 'absolute',
    inset: 0,
  },
  cardShimmer: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.3) 50%, transparent 70%)',
    backgroundSize: '200% 200%',
    animation: 'shimmer 2s ease-in-out infinite',
    transition: 'opacity 0.3s ease',
  },
  cardContent: {
    position: 'relative',
    zIndex: 1,
    padding: SHOP_SPACING.xl,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  cardIcon: {
    fontSize: '72px',
    marginBottom: SHOP_SPACING.md,
    filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))',
    display: 'inline-block',
  },
  cardTitle: {
    fontSize: '30px',
    fontWeight: '800',
    color: '#fff',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: SHOP_SPACING.sm,
    textShadow: '0 4px 8px rgba(0,0,0,0.3)',
  },
  cardDescription: {
    fontSize: '16px',
    color: 'rgba(255,255,255,0.95)',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: SHOP_SPACING.lg,
    lineHeight: 1.5,
    maxWidth: '280px',
  },
  cardButton: {
    border: '2px solid rgba(255,255,255,0.5)',
    borderRadius: '50px',
    padding: '14px 28px',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(10px)',
  },
  // Features banner - simple horizontal strip
  featuresBanner: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: SHOP_SPACING.xl,
    marginBottom: SHOP_SPACING.xxl,
    padding: `${SHOP_SPACING.lg} ${SHOP_SPACING.xl}`,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: SHOP_SPACING.sm,
  },
  featureIcon: {
    fontSize: '24px',
  },
  featureText: {
    fontSize: '15px',
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
  },
  // Products section - fun scattered polaroids
  productsSection: {
    marginBottom: SHOP_SPACING.xl,
    overflow: 'visible',
    padding: '0',
  },
  sectionTitle: {
    fontSize: '28px',
    fontWeight: '800',
    color: '#333',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: SHOP_SPACING.sm,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SHOP_SPACING.sm,
  },
  titleEmoji: {
    fontSize: '24px',
  },
  sectionSubtitle: {
    fontSize: '16px',
    color: '#666',
    fontFamily: FONT_FAMILY,
    textAlign: 'center',
    margin: 0,
    marginBottom: SHOP_SPACING.xl,
  },
  productsScroll: {
    display: 'flex',
    gap: SHOP_SPACING.lg,
    overflowX: 'auto',
    overflowY: 'visible',
    paddingBottom: '60px', // Extra space for tilted cards
    paddingTop: '40px',    // Extra space for lifted cards
    paddingLeft: SHOP_SPACING.xl,
    paddingRight: SHOP_SPACING.xl,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    justifyContent: 'center', // Center the cards
    WebkitOverflowScrolling: 'touch',
    justifyContent: 'center',
    alignItems: 'flex-end', // Align to bottom for wave effect
  },
  productStack: {
    flexShrink: 0,
    cursor: 'pointer',
    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  polaroid: {
    background: '#fff',
    borderRadius: '12px',
    padding: '12px',
    paddingBottom: '16px',
    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  polaroidPhoto: {
    width: '130px',
    height: '130px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  polaroidEmoji: {
    fontSize: '56px',
    transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
  },
  sparkleOverlay: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    fontSize: '20px',
    animation: 'sparkle 0.6s ease-in-out infinite',
  },
  polaroidLabel: {
    marginTop: '12px',
    textAlign: 'center',
  },
  polaroidText: {
    fontSize: '15px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
    transition: 'color 0.2s ease',
  },
  ctaBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SHOP_SPACING.lg,
    padding: SHOP_SPACING.xl,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '24px',
    flexWrap: 'wrap',
    textAlign: 'center',
  },
  ctaEmoji: {
    fontSize: '48px',
    animation: 'wiggle 2s ease-in-out infinite',
  },
  ctaTitle: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '800',
    color: '#fff',
    fontFamily: FONT_FAMILY,
  },
  ctaSubtitle: {
    margin: 0,
    marginTop: '4px',
    fontSize: '16px',
    color: 'rgba(255,255,255,0.9)',
    fontFamily: FONT_FAMILY,
  },
  ctaButton: {
    background: '#fff',
    border: 'none',
    borderRadius: '50px',
    padding: '16px 32px',
    fontSize: '18px',
    fontWeight: '700',
    color: '#764ba2',
    fontFamily: FONT_FAMILY,
    cursor: 'pointer',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    transition: 'all 0.3s ease',
  },
};

export default ShopHome;
